import { Observable, Subject } from 'rxjs';
import { SongApprovals, SongRights, SongRightsCredits } from 'src/app/shared/types/songs';
import { Recording } from '../recordings/state';
import { SearchRecordings } from '../recordings/state/recording.model';
import { IUser } from '../user/user.model';
import { InjectionToken } from '@angular/core';
import { Territory } from '../shared/projects';
import { ECountryCodes } from '../project/project-reports/project-reports.service';

export const SONG_API_SERVICE = new InjectionToken<SongApiService>('SongApiService');

export interface SongApiService {
  secondPlay$: Subject<number | string>;

  getTrackLink(id: number, download?: boolean): Observable<SongLink>;

  getDownloadLink(id: number): Observable<DownloadLink>;

  createDownloadLink(id?: number, downloadAll?: boolean): Observable<DownloadTask | ApiError>;

  download(id?: number, downloadAll?: boolean): Observable<DownloadFile>;

  getWaveform(id: number): any[] | null;

  requestSong(playlistId: number, id: number): Observable<any>;

  setPitchID?(pitchHash: string): void;

  getLyrics?(id: number): Observable<any>;

  getRights(songCode: string, territoryId: number): Observable<RightResponse>;

  getRightsCredits(songCode: string, territoryId: number): Observable<RightCreditsResponse>;

  getApprovals(songCode: string, territoryId: number): Observable<Array<ApprovalsResponse>>;

  requestUserInfo(): Observable<IUser>;

  updateUserAlbumArtFlag(flag: boolean): Observable<any>;

  getVersions(song: Recording, limit?: number): Observable<SearchRecordings | string>;

  getLyricsForWork?(workId: number | string): Observable<LyricResponse>;

  getWorkNotesForWork?(workId: number | string): Observable<WorKNotesResponse[]>;

  getSyncNotesForWork?(workId: number | string): Observable<any>;

  getTangoSyncNotes?(workId: string): Observable<any>;

  getAssetNotesForRecordings?(recordingId: number | string): Observable<any>;

  getRightsTerritories?(): Observable<Territory[]>;

  downloadLyricsAsPDF?(id: number): Observable<any>;

  downloadWorkLyricsAsPDF?(workId: number): Observable<any>;

  refreshWork?(workId: number | string): Observable<any>;
}

export interface SongLink {
  content?: string | null;
  error?: Error;
}

export interface DownloadTask {
  success: boolean;
  id: number | null;
}

export interface DownloadLink {
  status: string;
  link?: string;
}

export interface DownloadLinkWithProgressBar {
  status: string;
  link?: string;
  progress: {
    downloadProgress: number;
    zippingProgress: number;
    uploadProgress: number;
  };
}

export type TSongMenuInfoItem = {
  artists: string[];
  recordingTranslations: string[];
  albumTitle: string[];
  originalAlbumTitle: string[];
  releaseYear: string[];
  label: string[];
  writers: string[];
  copyrightYear: string[];
  isrc: string[];
  songCode: string[];
  genres: string[];
  bpm: string[];
  themes: string[];
  versions: string[];
  vocalMix: string[];

  consumerOgrd?: any[];
  workCode?: any[];
  gpi?: any[];
};

export type TWorkMenuInfoItem = Pick<TSongMenuInfoItem, 'writers' | 'songCode' | 'copyrightYear' | 'themes'>;
export type TMLRWorkMenuInfoItem = Pick<TSongMenuInfoItem, 'writers' | 'workCode' | 'copyrightYear' | 'themes'>;

export type TSongMenuInfoLabelItem = {
  artists: string;
  recordingTranslations: string;
  albumTitle: string;
  originalAlbumTitle: string;
  releaseYear: string;
  label: string;
  writers: string;
  copyrightYear: string;
  isrc: string;
  songCode: string;
  genres: string;
  bpm: string;
  themes: string;
  versions: string;
  vocalMix: string;

  consumerOgrd?: any;
  workCode?: string;
  gpi?: string;
};

export type TWorkMenuInfoLabelItem = Pick<TSongMenuInfoLabelItem, 'writers' | 'songCode' | 'copyrightYear' | 'themes'>;
export type TMLRWorkMenuInfoLabelItem = Pick<
  TSongMenuInfoLabelItem,
  'writers' | 'workCode' | 'copyrightYear' | 'themes'
>;

export interface LyricResponse {
  chorus: string[];
  0: string;
  id: number;
  language: string;
  text: string[];
  source?: string;
}

export interface WorKNotesResponse {
  id: number;
  isoCountry: ECountryCodes;
  noteTypes: { id: number; field: string; description: string };
  note: string;
  source?: string;
}

export interface SyncNotesResponse {
  createdDate: string;
  modifiedDate: string;
  id: number;
  note: string;
  source?: string;
  sourceWorkId: string;
  userDto: IUser;
}

export interface AssetNotesResponse {
  id: number;
  sharpId: number;
  noteTypeId: number;
  note: string;
  recordingId: string;
  isManualUpload: boolean;
  userId: number;
  creationDate: string;
}

export type RightResponse = SongRights;

export type RightCreditsResponse = SongRightsCredits;

export type ApprovalsResponse = SongApprovals;

export interface RightErrorResponse {
  [key: string]: string;
  error: string;
}

export interface DownloadFile {
  source: Blob;
  link: string;
}

export interface ApiError {
  content: any;
  error: any;
}

export enum SongTagType {
  default = 'default',
  private = 'private',
  watermarked = 'watermarked',
}
export interface SongTag {
  label: string;
  type: SongTagType;
}

export interface ITangoSyncNote {
  comment: string;
  createdAt: string;
  id: number;
  reason: string;
  type: string;
  userName: string;
  workCode: string;
}
